import { toggleOffcanvas } from "@/utils/Helper";
import { useOffcanvasStore } from "@/store/useOffcanvasStore";
import { Component, defineAsyncComponent } from "vue";

const PaymentMethods = defineAsyncComponent(() => import("@/views/OffcanvasViews/PaymentMethods/PaymentMethods.vue"));
const CardsList = defineAsyncComponent(() => import("@/views/OffcanvasViews/CreditCards/CreditCards.vue"));
const AddCard = defineAsyncComponent(() => import("@/views/OffcanvasViews/AddCreditCard/AddCreditCard.vue"));
const More = defineAsyncComponent(() => import("@/views/More/MoreMenu.vue"));
const AboutApp = defineAsyncComponent(() => import("@/views/More/AboutApp/AboutApp.vue"));
const Help = defineAsyncComponent(() => import("@/views/OffcanvasViews/Help/Help.vue"));
const NotificationsList = defineAsyncComponent(() => import("@/views/OffcanvasViews/Notifications/Notifications.vue"));
const PartnershipDetail = defineAsyncComponent(() => import("@/views/OffcanvasViews/Partnership/Partnership.vue"));
const MyAccount = defineAsyncComponent(() => import("@/views/More/MyAccount/MyAccount.vue"));
const AccountUpdate = defineAsyncComponent(() => import("@/views/More/MyAccount/AccountUpdate/AccountUpdate.vue"));
const AppointmentDetail = defineAsyncComponent(() => import("@/views/Appointments/AppointmentDetail/AppointmentDetail.vue"));
const PsiProfile = defineAsyncComponent(() => import("@/views/PsiDetail/PsiDetail.vue"));
const Checkout = defineAsyncComponent(() => import("@/views/Checkout/Checkout.vue"));
const ScheduleDate = defineAsyncComponent(() => import("@/views/AppointmentScheduleDate/AppointmentScheduleDate.vue"));
const PsiRatings = defineAsyncComponent(() => import("@/views/PsiDetail/PsiRatings.vue"));
const PasswordUpdate = defineAsyncComponent(() => import("@/views/More/MyAccount/PasswordUpdate/PasswordUpdate.vue"));

export enum EOffcanvasViews {
  paymentMethods = "paymentMethods",
  addCreditCard = "addCreditCard",
  creditCards = "creditCards",
  more = "more",
  help = "help",
  notifications = "notifications",
  partnershipDetail = "partnershipDetail",
  myAccount = "myAccount",
  accountUpdate = "accountUpdate",
  passwordUpdate = "passwordUpdate",
  about = "about",
  appointmentDetail = "appointmentDetail",
  psiRatings = "psiRatings",
  psiProfile = "psiProfile",
  scheduleDate = "scheduleDate",
  checkout = "checkout",
}

export interface IOffcanvasConfig {
  title: string;
  path: OffcanvasViewKey | string;
  detail_id?: number;
  props?: any;
}

interface IOffcanvasPages {
  title: string;
  component: Component;
}

export type OffcanvasViewKey = keyof typeof EOffcanvasViews;
export type IOffcanvasViewsConfig = Record<OffcanvasViewKey, IOffcanvasConfig>;

const pages: Record<OffcanvasViewKey, IOffcanvasPages> = {
  [EOffcanvasViews.paymentMethods]: { title: "Métodos de pagamento", component: PaymentMethods },
  [EOffcanvasViews.creditCards]: { title: "Cartões de crédito", component: CardsList },
  [EOffcanvasViews.addCreditCard]: { title: "Adicionar cartão", component: AddCard },
  [EOffcanvasViews.more]: { title: "Mais", component: More },
  [EOffcanvasViews.help]: { title: "Ajuda", component: Help },
  [EOffcanvasViews.notifications]: { title: "Notificações", component: NotificationsList },
  [EOffcanvasViews.partnershipDetail]: { title: "Benefícios", component: PartnershipDetail },
  [EOffcanvasViews.myAccount]: { title: "Minha conta", component: MyAccount },
  [EOffcanvasViews.accountUpdate]: { title: "Editar conta", component: AccountUpdate },
  [EOffcanvasViews.passwordUpdate]: { title: "Alterar senha", component: PasswordUpdate },
  [EOffcanvasViews.about]: { title: "Sobre", component: AboutApp },
  [EOffcanvasViews.appointmentDetail]: { title: "Detalhes da sessão", component: AppointmentDetail },
  [EOffcanvasViews.psiRatings]: { title: "Depoimentos", component: PsiRatings },
  [EOffcanvasViews.psiProfile]: { title: "Perfil do psi", component: PsiProfile },
  [EOffcanvasViews.scheduleDate]: { title: "Selecione uma data", component: ScheduleDate },
  [EOffcanvasViews.checkout]: { title: "Finalizar pagamento", component: Checkout },
};

export const OffcanvasViewsConfig = Object.fromEntries(
  Object.entries(pages).map(([key, value]) => [
    key,
    { path: key as OffcanvasViewKey, title: value.title },
  ]),
) as IOffcanvasViewsConfig;

export const Views = Object.fromEntries(
  Object.entries(pages).map(([key, value]) => [key, value.component]),
) as Record<OffcanvasViewKey, Component>;

/**
 * Mesma assinatura do pro. Diferença: no main, o padrão de `toggle` é abrir o
 * painel só quando o histórico está vazio (comportamento que todas as telas do
 * main esperam). No pro o padrão é `true` e quem já está dentro passa `false`.
 */
export const navigateCanvas = (
  to: OffcanvasViewKey,
  config?: IOffcanvasConfig | null,
  toggle?: boolean,
) => {
  const offcanvasStore = useOffcanvasStore();
  const shouldToggle = toggle ?? !offcanvasStore.history.length;

  const last = JSON.stringify(offcanvasStore.history);
  const conf = config || OffcanvasViewsConfig[to];

  if (last === JSON.stringify(conf)) return;

  if (shouldToggle) toggleOffcanvas("offcanvas_views");

  offcanvasStore.save(conf);
};

/** Detalhe da sessão no painel lateral, como no pro (o id vai no `detail_id`). */
export const openAppointmentDetail = (id: number | string, toggle?: boolean) =>
  navigateCanvas(
    EOffcanvasViews.appointmentDetail,
    { ...OffcanvasViewsConfig[EOffcanvasViews.appointmentDetail], detail_id: Number(id) },
    toggle,
  );

/** Perfil do psi no painel lateral (id ou slug). Dentro de outro painel, empilha: a seta volta. */
export const openPsiProfile = (idOrSlug: number | string, toggle?: boolean) =>
  navigateCanvas(
    EOffcanvasViews.psiProfile,
    { ...OffcanvasViewsConfig[EOffcanvasViews.psiProfile], props: { id: String(idOrSlug) } },
    toggle,
  );

/** Checkout no painel; o título muda na sessão imediata ("Sessão imediata"). */
export const openCheckout = (title?: string, toggle?: boolean) =>
  navigateCanvas(
    EOffcanvasViews.checkout,
    { ...OffcanvasViewsConfig[EOffcanvasViews.checkout], props: title ? { title } : undefined },
    toggle,
  );

const PAYMENT_VIEWS: string[] = [EOffcanvasViews.paymentMethods, EOffcanvasViews.creditCards, EOffcanvasViews.addCreditCard];

/**
 * Terminou de escolher/cadastrar o pagamento: volta para quem abriu (ex.: o checkout), tirando do
 * painel as telas de pagamento empilhadas. Aberto direto (Mais), fecha o painel como antes.
 */
export const leavePaymentFlow = () => {
  const store = useOffcanvasStore();
  while (store.history.length && PAYMENT_VIEWS.includes(String(store.history[store.history.length - 1]?.path))) {
    store.popHistory();
  }
  if (!store.history.length) closeOffCanvas();
};

/** Troca tudo o que está no painel por outra tela, sem fechar e abrir de novo (ex.: checkout → detalhe da sessão). */
export const replaceCanvas = (to: OffcanvasViewKey, config?: IOffcanvasConfig | null) => {
  const store = useOffcanvasStore();
  const open = document.getElementById("offcanvas_views")?.className.includes("show");
  if (!open) return navigateCanvas(to, config, true);
  store.clear();
  store.save(config || OffcanvasViewsConfig[to]);
};

export const goBackCanvas = () => {
  const offcanvasStore = useOffcanvasStore();
  const toggle = offcanvasStore.history.length == 1;
  if (toggle) {
    closeOffCanvas();
  } else {
    offcanvasStore.popHistory();
  }
};

export const closeOffCanvas = () => {
  useOffcanvasStore().clear();
  const offCanvasRef = document.getElementById("offcanvas_views");
  const hasOffCanvasOpened = offCanvasRef?.className.includes("show");
  if (hasOffCanvasOpened) {
    toggleOffcanvas("offcanvas_views");
  }
};
